import { Observable, catchError, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EngineReportFilter, EngineReportsResponse } from '@navstation-portal/reporting/data-model/engine-reports';
import { AuthStore } from '@navstation-portal/shared/data-access/auth';
import { ApiErrorMapper, BASE_API_URL } from '@navstation-portal/shared/util/api';

@Injectable({ providedIn: 'root' })
export class EngineReportsService {
  private http = inject(HttpClient);
  private authStore = inject(AuthStore);
  private readonly baseUrl = inject(BASE_API_URL);
  private apiError = inject(ApiErrorMapper).forFeature('EngineReports');

  getEngineReports(vesselId?: string): Observable<EngineReportsResponse> {
    if (!this.authStore.isLoggedIn()) {
      return throwError(() => new Error('Not logged in.'));
    }

    if (!vesselId) {
      return throwError(() => new Error('vesselId is required'));
    }

    return this.http
      .get<EngineReportsResponse>(`${this.baseUrl}/${vesselId}/engineReports`)
      .pipe(catchError(this.apiError.toApiError('getting engine reports')));
  }

  /**
   * Downloads a CSV extract of engine reports for a single vessel.
   *
   * NOTE: this mirrors the V1 contract (`engineReport.service.js#extractData`,
   * `GET {baseUrl}extractEngineReports?vessel_ids=...&report_filter=...`,
   * returning raw CSV text) as a best guess. The V2 backend endpoint has not
   * been confirmed to exist yet at this path/shape - verify before merging.
   */
  extractEngineReports(vesselId: string, reportFilter: EngineReportFilter): Observable<Blob> {
    if (!this.authStore.isLoggedIn()) {
      return throwError(() => new Error('Not logged in.'));
    }

    return this.http
      .get(`${this.baseUrl}/extractEngineReports`, {
        params: { vessel_ids: vesselId, report_filter: reportFilter },
        responseType: 'blob',
      })
      .pipe(catchError(this.apiError.toApiError('extracting engine reports')));
  }
}
